import type { Unsubscribe } from './disposable.js';

/**
 * Minimal immutable state store.
 *
 * WHY: unidirectional data flow — views subscribe and re-render on change; state
 * is replaced via an immutable updater, never mutated in place. Underpins
 * replay/cassette later. Selectors read derived slices.
 */
export interface Store<S> {
  getState(): S;
  /** Replace state with `updater(prev)`; notifies subscribers if it changed. */
  update(updater: (prev: S) => S): void;
  /** Subscribe to state changes; returns an unsubscribe. */
  subscribe(listener: (state: S) => void): Unsubscribe;
  /** Read a derived slice. */
  select<T>(selector: (state: S) => T): T;
}

export function createStore<S>(initial: S): Store<S> {
  let state = initial;
  const listeners = new Set<(state: S) => void>();
  return {
    getState: () => state,
    update(updater) {
      const next = updater(state);
      if (Object.is(next, state)) return;
      state = next;
      for (const listener of [...listeners]) listener(state);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    select(selector) {
      return selector(state);
    },
  };
}
